/**
 * @file     rentalCardModel.ts
 * @readme   ../CLAUDE.md
 * @purpose  Pure rules behind the rental card: the availability badge (now or
 *           a date, judged on the Vancouver calendar), the pets, furnished and
 *           short-term flags read from MLS® feed tokens or an owner listing's
 *           fields, and the owner home-type label. No React, so the card, the
 *           row and the tests share one answer.
 */
import { formatMonthDay, formatShortDate, partsOf } from '@/utils/dateFormat';

export type RentalFeatures = { pets: boolean; furnished: boolean; shortTerm: boolean };

export type Availability = { kind: 'now' } | { kind: 'date'; label: string };

const DATE_RE = /^\d{4}-\d{2}-\d{2}/;

/** Today on the Vancouver calendar, as YYYY-MM-DD. */
export function vancouverToday(now: Date): string {
  const p = partsOf(now);
  if (!p) return now.toISOString().slice(0, 10);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/**
 * "Available now" when there is no date or the date is today or past; else
 * the date ("Nov 1", with the year when it is not this year). `now` null is
 * the server render and first client pass: a dated listing shows its date
 * until the clock is known, so the two renders agree.
 */
export function availabilityOf(availableOn: string | null | undefined, now: Date | null): Availability {
  const iso = availableOn && DATE_RE.test(availableOn) ? availableOn.slice(0, 10) : null;
  if (!iso) return { kind: 'now' };
  if (now) {
    const today = vancouverToday(now);
    if (iso <= today) return { kind: 'now' };
    if (iso.slice(0, 4) !== today.slice(0, 4)) return { kind: 'date', label: formatShortDate(iso) };
  }
  return { kind: 'date', label: formatMonthDay(iso) };
}

const PET_OK_TOKENS = new Set(['yes', 'cats ok', 'dogs ok']);

/** MLS® feed tokens: PetsAllowed contains Yes / Cats OK / Dogs OK; Furnished is "Furnished"; LeaseTerm "Short Term Lease". */
export function mlsRentalFeatures(input: {
  petsAllowed?: readonly string[] | null;
  furnished?: string | null;
  leaseTerm?: string | null;
}): RentalFeatures {
  return {
    pets: (input.petsAllowed ?? []).some((token) => PET_OK_TOKENS.has(token.trim().toLowerCase())),
    furnished: input.furnished?.trim() === 'Furnished',
    shortTerm: input.leaseTerm?.trim() === 'Short Term Lease',
  };
}

/** Owner listing fields (listingKinds lease): pets yes / cats / dogs, furnished "furnished". */
export function ownerRentalFeatures(input: { pets?: string | null; furnished?: string | null }): RentalFeatures {
  return {
    pets: input.pets === 'yes' || input.pets === 'cats' || input.pets === 'dogs',
    furnished: input.furnished === 'furnished',
    shortTerm: false,
  };
}

const OWNER_HOME_TYPES: Record<string, string> = {
  apartment_condo: 'Apartment/Condo',
  townhouse: 'Townhouse',
  house: 'House',
  suite: 'Suite',
  laneway: 'Laneway house',
  duplex: 'Duplex',
};

/** The owner home type as the card's place line prints it; "other" and unknown print nothing. */
export function ownerHomeTypeLabel(homeType: string | null | undefined): string | null {
  return homeType ? OWNER_HOME_TYPES[homeType] ?? null : null;
}

/** Owner street line: "#1203, Ontario Street". The street number is never public. */
export function ownerStreetLine(address: { streetName: string; unit: string | null }): string {
  const unit = address.unit?.trim();
  return unit ? `#${unit.replace(/^#/, '')}, ${address.streetName}` : address.streetName;
}

/** Fair-rent band position, supplied by another surface. The chip renders only when known. */
export type FairRentBand = 'in' | 'below' | 'above';
